import { useEffect, useState } from "react";
import { IconCalendar, IconChevronDown, IconPlus } from "@pentabd/icons";
import {
  Button,
  FileInfoCard,
  FileUpload,
  InputDate,
  InputSelect,
  InputText,
  Select,
  Text,
  TextArea,
} from "@pentabd/ui";

import { useTranslation } from "react-i18next";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import {
  ProcurementCreateType,
  ProcurementResponseType,
} from "@type/procurements/procurement";
import { procurementDetailsValidation } from "@validations/Procurement/procurement/procurementValidation";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Comments,
  ErrorModal,
  InputDateControllerRow,
  InputTextRow,
  MultiFile,
  MultiFileRow,
  NextButton,
  SelectControllerRow,
  SuccessModal,
} from "@components";

import { PROCUREMENT_DETAILS } from "@validations/Procurement/procurement/procurementValidation";
import { getConvertedData } from "@helpers/baseDataConvert";
import useActionCreator from "@redux/hooks/useActionCreator";
import useCustomSelector from "@redux/hooks/useCustomSelector";
import { RichTextEditor } from "./RichTextEditor";
import ProcurementDemandList from "./ProcurementDemandList";
import { useSelectedProducts } from "@hooks/procurements/procurement/useSelectedProducts";
import { useMetaData } from "@hooks/procurements/useMetaData";
import { useUser } from "@hooks/useUser";
import { getAccess } from "@helpers/permissionController";
import { SelectedProductTable } from "./SecondaryTable/SelectedProductsTable";
import { ROUTES } from "@constants/routes";
import { FORM_FIELDS } from "@constants/form";
import { useNavigate } from "react-router-dom";
import { AnnualProcurementType } from "@type/procurements/annualProcurementPlan";
import { PackageType } from "@type/procurements/package";
import { getQuarterBudget } from "@helpers/procurement/getQuarterBudget";
import {
  convertMultipleDocumentData,
  revertMultipleDocumentData,
} from "@utils/documentDataConvert";

type SelectedProductType = {
  [key: string]: any;
  unitPrice: number;
  quantity: number;
};

interface GeneralInfoFormProps {
  id: string | undefined;
  procurement: ProcurementResponseType | null;
  changeActiveTab: (arg: number) => void;
  // setIsSuccessModalOpen: (arg: boolean) => void;
  // setIsErrorModalOpen: (arg: boolean) => void;
  setSearchParams: Function;
}

const PACKAGE = FORM_FIELDS.PROCUREMENT_PACKAGE;

export const QUARTER_DATA = [
  {
    label: "First Quarter",
    value: "FIRST_QUARTER",
    response: PACKAGE.FIRST_QUARTER_BUDGET,
  },
  {
    label: "Second Quarter",
    value: "SECOND_QUARTER",
    response: PACKAGE.SECOND_QUARTER_BUDGET,
  },
  {
    label: "Third Quarter",
    value: "THIRD_QUARTER",
    response: PACKAGE.THIRD_QUARTER_BUDGET,
  },
  {
    label: "Fourth Quarter",
    value: "FOURTH_QUARTER",
    response: PACKAGE.FOURTH_QUARTER_BUDGET,
  },
];

export const GeneralInfoForm = ({
  id,
  procurement,
  changeActiveTab,
  // setIsSuccessModalOpen,
  // setIsErrorModalOpen,
  setSearchParams,
}: GeneralInfoFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [demandIds, setDemandIds] = useState<number[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<
    SelectedProductType[] | []
  >([]);

  // modals
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  // permissions
  const { user } = useUser();
  const { realm_roles: roles } = user || {};
  const [canInitiateProcurement, setCanInitiateProcurement] = useState(false);

  //error message
  const [errorMessage, setErrorMessage] = useState<string | null>("");

  const {
    packageFetchActionHandler,
    procurementCreateActionHandler,
    packageByIdFetchActionHandler,
    procurementUpdateActionHandler,
  } = useActionCreator();

  const { resData } = useMetaData("apps");
  const { getData: packageData, getSingleData: packageSingleData } =
    useCustomSelector((store) => store.procurements.packageState);

  const { data: packages } = packageData || {};

  const {
    createSuccess,
    createError,
    getSingleData,
    updateError,
    updateSuccess,
    deleteError,
    deleteSuccess,
    isCreateLoading,
    isUpdateLoading,
  } = useCustomSelector((store) => store.procurements.procurementState);

  const methods = useForm<ProcurementCreateType>({
    resolver: yupResolver(procurementDetailsValidation),
    values: revertMultipleDocumentData(getSingleData, [
      PROCUREMENT_DETAILS.DOCUMENTS,
    ]) as ProcurementResponseType,
  });

  const {
    register,
    formState: { errors },
    control,
    setValue,
    reset,
    getValues,
    watch,
    unregister,
  } = methods;

  const { append, fields } = useFieldArray({
    control,
    name: "procurementItems",
  });

  const {
    handleQuery: handleSelectedProductQuery,
    selectedProducts: selectedProductsPerDemand,
  } = useSelectedProducts();

  // demand ids check and add
  const handleSelectedDemand = (id: number) => {
    if (demandIds.includes(id)) {
      const filteredData = demandIds.filter((item: number) => item !== id);
      setDemandIds(filteredData);
    } else {
      setDemandIds((prev) => [...prev, id]);
    }
  };

  const onSubmit: SubmitHandler<ProcurementCreateType> = (data: any) => {
    if (id) {
      const reqData = {
        nameEn: data?.nameEn,
        nameBn: data?.nameBn,
        procurementRefNo: data?.procurementRefNo,
        appId: data?.appId,
        appPackageId: data?.appPackageId,
        packageBudgetId: data?.packageBudgetId,
        budgetQuarter: data?.budgetQuarter,
        letter: data?.letter,
        publishDate: data?.publishDate,
        lastSubmissionDate: data?.lastSubmissionDate,
        completionDate: data?.completionDate,
        procurementDocuments: data?.procurementDocuments,
        procurementItems: data?.procurementItems,
        procurementDemandIds: data?.procurementDemands?.map(
          (demand: any) => demand?.id,
        ),
      };
      procurementUpdateActionHandler(
        id,
        convertMultipleDocumentData(reqData, [PROCUREMENT_DETAILS.DOCUMENTS]),
      );
    } else {
      const reqData = {
        ...data,
        procurementDemandIds: demandIds,
      };
      procurementCreateActionHandler(
        convertMultipleDocumentData(reqData, [PROCUREMENT_DETAILS.DOCUMENTS]),
      );
    }
  };

  useEffect(() => {
    handleSelectedProductQuery("demandIds", demandIds);
  }, [demandIds]);

  useEffect(() => {
    setSelectedProduct(selectedProductsPerDemand);
  }, [selectedProductsPerDemand]);

  useEffect(() => {
    setCanInitiateProcurement(getAccess(roles, "PROCUREMENT_INITIATE"));

    if (procurement?.appId) {
      const query = {
        id: procurement.appId,
        page: 1,
        size: 10000,
      };
      packageFetchActionHandler(query);
    }
    if (procurement?.appPackageId) {
      packageByIdFetchActionHandler(procurement.appPackageId);
    }

    if (procurement) {
      setValue("appBudget", procurement?.app?.budget);
      setValue("packageBudget", procurement?.appPackage?.allocatedBudget);
      setValue(
        "quarterBudget",
        getQuarterBudget(
          [{ ...procurement?.packageBudget }],
          procurement?.budgetQuarter,
          procurement?.packageBudgetId,
        ) || 0,
      );
    }

    if (procurement?.procurementDetails?.procurementChalanDocuments) {
      setValue(
        "procurementChalanDocuments",
        procurement?.procurementDetails?.procurementChalanDocuments,
      );
    }

    if (procurement?.procurementDetails?.procurementDetailsDocuments) {
      setValue(
        "procurementDetailsDocuments",
        procurement?.procurementDetails?.procurementDetailsDocuments,
      );
    }

    if (procurement?.procurementDetails?.procurementWarrantyDocuments) {
      setValue(
        "procurementWarrantyDocuments",
        procurement?.procurementDetails?.procurementWarrantyDocuments,
      );
    }

    if (procurement?.procurementDetails?.advertisementDate) {
      setValue(
        "advertisementDate",
        procurement?.procurementDetails?.advertisementDate,
      );
    }

    if (procurement?.procurementDetails?.awardedDate) {
      setValue("awardedDate", procurement?.procurementDetails?.awardedDate);
    }
  }, [procurement]);

  useEffect(() => {
    reset({
      ...getValues(),
      procurementItems: selectedProduct,
    });

    // selectedProductsPerDemand?.forEach((item: any) => {
    //   append(item);
    // });
  }, [selectedProduct]);

  const packageBudgetId = watch(PROCUREMENT_DETAILS.PACKAGE_BUDGET_ID);

  useEffect(() => {
    switch (true) {
      case createSuccess: {
        setIsSuccessModalOpen(true);
        !id &&
          setSearchParams((params: any) => {
            params.set("id", getSingleData?.id);
            return params;
          });
        break;
      }
      case updateSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case deleteSuccess: {
        setIsSuccessModalOpen(true);
        break;
      }
      case !!createError: {
        setErrorMessage(createError?.response?.data?.message);

        setIsErrorModalOpen(true);
        break;
      }
      case !!updateError: {
        setErrorMessage(createError?.response?.data?.message);
        setIsErrorModalOpen(true);
        break;
      }
      case !!deleteError: {
        setErrorMessage(createError?.response?.data?.message);
        setIsErrorModalOpen(true);
        break;
      }
      default:
        break;
    }
  }, [
    createSuccess,
    createError,
    updateSuccess,
    updateError,
    deleteError,
    deleteSuccess,
  ]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    navigate(`/procurement/${id ? id : getSingleData?.id}`);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  return (
    <div>
      {/* MODAL SECTION */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={errorMessage || ""}
        />
      </>
      <FormProvider {...methods}>
        <form
          className="container d-flex flex-column gap-6 pt-10"
          onSubmit={methods.handleSubmit(onSubmit)}
        >
          <InputTextRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.NAME_EN"
            register={{ ...register(PROCUREMENT_DETAILS.NAME_EN) }}
            error={errors as any}
            required
          />

          <InputTextRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.NAME_BN"
            register={{ ...register(PROCUREMENT_DETAILS.NAME_BN) }}
            error={errors as any}
            required
          />

          <InputTextRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.REF_NO"
            register={{ ...register(PROCUREMENT_DETAILS.REF_NO) }}
            error={errors as any}
            required
          />

          {/* <InputDateControllerRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.PUBLISH_DATE"
            control={control}
            register={{ ...register(PROCUREMENT_DETAILS.PUBLISH_DATE) }}
            errors={errors as any}
            name={PROCUREMENT_DETAILS.PUBLISH_DATE}
            required
            disabled={id ? true : false}
          />

          <InputDateControllerRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.COMPLETION_DATE"
            control={control}
            register={{ ...register(PROCUREMENT_DETAILS.COMPLETION_DATE) }}
            errors={errors as any}
            name={PROCUREMENT_DETAILS.COMPLETION_DATE}
            required
            disabled={id ? true : false}
          />

          <InputDateControllerRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.LAST_SUBMISSION_DATE"
            control={control}
            register={{ ...register(PROCUREMENT_DETAILS.LAST_SUBMISSION_DATE) }}
            errors={errors as any}
            name={PROCUREMENT_DETAILS.LAST_SUBMISSION_DATE}
            required
            disabled={id ? true : false}
          /> */}

          {/* value of package will be changed based on app */}
          <div className="row">
            <div className="col-md-12 col-lg-3">
              <Text weight="semibold" size="sm" color="title">
                {t("PROCUREMENT_CREATE.GENERAL_INFO.FORM.APP_ID.LABEL")}
              </Text>
              <span className="text-danger">&#42;</span>
            </div>
            <div className="col-md-12 col-lg-4">
              <Controller
                control={control}
                name={PROCUREMENT_DETAILS.APP_ID}
                render={({ field }) => {
                  return (
                    <InputSelect
                      {...register(PROCUREMENT_DETAILS.APP_ID)}
                      options={getConvertedData(resData?.data)}
                      defaultValue={field.value as number}
                      onSelectItem={(e) => {
                        const query = {
                          id: e,
                          page: 1,
                          size: 10000,
                        };
                        packageFetchActionHandler(query);
                        setValue(PROCUREMENT_DETAILS.APP_ID, e as number);

                        // set the app budget to show

                        const appBudget = resData?.data?.find(
                          (item: AnnualProcurementType) => item.id === e,
                        );

                        setValue("appBudget", appBudget?.budget || 0);
                        unregister("appBudget");
                      }}
                      suffix={<IconChevronDown size="16" />}
                      placeholder={t(
                        "PROCUREMENT_CREATE.GENERAL_INFO.FORM.APP_ID.PLACEHOLDER",
                      )}
                      error={errors as any}
                      disabled={id ? true : false}
                    />
                  );
                }}
              />
            </div>
            <div className="col-md-12 col-lg-3">
              <InputText
                {...register("appBudget")}
                name="appBudget"
                disabled
                suffix={t("PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_3.SUFFIX")}
              />
            </div>
          </div>

          {/* value of package will be changed based on app */}
          {packages && (
            <div className="row">
              <div className="col-md-12 col-lg-3">
                <Text weight="semibold" size="sm" color="title">
                  {t(
                    "PROCUREMENT_CREATE.GENERAL_INFO.FORM.APP_PACKAGE_ID.LABEL",
                  )}
                </Text>
                <span className="text-danger">&#42;</span>
              </div>
              <div className="col-md-12 col-lg-4">
                <Controller
                  control={control}
                  name={PROCUREMENT_DETAILS.APP_PACKAGE_ID}
                  render={({ field }) => {
                    return (
                      <InputSelect
                        {...register(PROCUREMENT_DETAILS.APP_PACKAGE_ID)}
                        options={getConvertedData(packages)}
                        defaultValue={field.value as number}
                        onSelectItem={(e) => {
                          packageByIdFetchActionHandler(e as number);
                          setValue(
                            PROCUREMENT_DETAILS.APP_PACKAGE_ID,
                            e as number,
                          );

                          // set package budget
                          const packageBudget = packages?.find(
                            (item: PackageType) => item.id === e,
                          );
                          setValue(
                            "packageBudget",
                            packageBudget?.allocatedBudget || 0,
                          );
                          unregister("packageBudget");
                        }}
                        suffix={<IconChevronDown size="16" />}
                        placeholder={t(
                          "PROCUREMENT_CREATE.GENERAL_INFO.FORM.APP_PACKAGE_ID.PLACEHOLDER",
                        )}
                        error={errors as any}
                        disabled={id ? true : false}
                      />
                    );
                  }}
                />
              </div>
              <div className="col-md-12 col-lg-3">
                <InputText
                  {...register("packageBudget")}
                  name="packageBudget"
                  disabled
                  suffix={t("PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_3.SUFFIX")}
                />
              </div>
            </div>
          )}

          {packageSingleData && (
            <>
              <SelectControllerRow
                label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.PACKAGE_BUDGET_ID"
                control={control}
                name={PROCUREMENT_DETAILS.PACKAGE_BUDGET_ID}
                register={{
                  ...register(PROCUREMENT_DETAILS.PACKAGE_BUDGET_ID),
                }}
                options={
                  getConvertedData(packageSingleData?.packageBudgetDetails) ||
                  []
                }
                setValue={setValue}
                error={errors as any}
                disabled={id ? true : false}
                required
              />

              <div className="row">
                <div className="col-md-12 col-lg-3">
                  <Text weight="semibold" size="sm" color="title">
                    {t(
                      "PROCUREMENT_CREATE.GENERAL_INFO.FORM.PACKAGE_BUDGET_QUARTER.LABEL",
                    )}
                  </Text>
                  <span className="text-danger">&#42;</span>
                </div>
                <div className="col-md-12 col-lg-4">
                  <Controller
                    control={control}
                    name={PROCUREMENT_DETAILS.PACKAGE_BUDGET_QUARTER}
                    render={({ field }) => {
                      return (
                        <InputSelect
                          {...register(
                            PROCUREMENT_DETAILS.PACKAGE_BUDGET_QUARTER,
                          )}
                          options={QUARTER_DATA}
                          defaultValue={field.value as number}
                          onSelectItem={(e) => {
                            setValue(
                              PROCUREMENT_DETAILS.PACKAGE_BUDGET_QUARTER,
                              e,
                            );

                            // set package budget
                            const quarterBudget = getQuarterBudget(
                              packageSingleData?.packageBudgetDetails,
                              e as string,
                              packageBudgetId,
                            );
                            setValue("quarterBudget", quarterBudget || 0);
                            unregister("quarterBudget");
                          }}
                          suffix={<IconChevronDown size="16" />}
                          placeholder={t(
                            "PROCUREMENT_CREATE.GENERAL_INFO.FORM.PACKAGE_BUDGET_QUARTER.PLACEHOLDER",
                          )}
                          error={errors as any}
                          disabled={id ? true : false}
                        />
                      );
                    }}
                  />
                </div>
                <div className="col-md-12 col-lg-3">
                  <InputText
                    {...register("quarterBudget")}
                    name="quarterBudget"
                    disabled
                    suffix={t("PROCUREMENT_ADD_NEW.FORM.FIELDS.FIELD_3.SUFFIX")}
                  />
                </div>
              </div>
            </>
          )}

          {/* <RichTextEditor disable={id ? true : false} /> */}

          {procurement?.procurementCommittee && (
            <InputTextRow
              label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.SELECTED_COMMITTEE"
              register={{ ...register("committee") }}
              disabled
              value={procurement?.procurementCommittee?.name}
              controlling
            />
          )}
          {procurement?.procurementDetails && (
            <>
              <InputTextRow
                label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.ACTUAL_COST"
                register={{ ...register("actualCOst") }}
                disabled
                value={procurement?.procurementDetails?.actualCost}
                controlling
              />

              <InputDateControllerRow
                label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.ADVERTISEMENT_DATE"
                control={control}
                register={{ ...register("advertisementDate") }}
                name={"advertisementDate"}
                required
                disabled
              />

              <InputDateControllerRow
                label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.AWARDED_DATE"
                control={control}
                register={{ ...register("awardedDate") }}
                name={"awardedDate"}
                required
                disabled
              />

              {procurement?.procurementDetails?.committeeRemarks && (
                <InputTextRow
                  label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.COMMITTEE_REMARKS"
                  register={{ ...register("committeeRemarks") }}
                  disabled
                  value={procurement?.procurementDetails?.committeeRemarks}
                  controlling
                />
              )}

              {procurement?.procurementDetails?.storeInChargeRemarks && (
                <InputTextRow
                  label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.STORE_IN_CHARGE_REMARKS"
                  register={{ ...register("storeInChargeRemarks") }}
                  disabled
                  value={procurement?.procurementDetails?.storeInChargeRemarks}
                  controlling
                />
              )}

              {procurement?.procurementDetails?.procurementChalanDocuments
                ?.length ? (
                <div className="row">
                  <div className="col-md-12 col-lg-3">
                    <Text weight="semibold" size="sm" color="title">
                      {t(
                        "PROCUREMENT_CREATE.GENERAL_INFO.FORM.CHALAN_DOCUMENTS.LABEL",
                      )}
                    </Text>
                  </div>
                  <div className="col-md-12 col-lg-7">
                    <MultiFile
                      id="procurementChalanDocuments"
                      control={control}
                      setValue={setValue}
                      register={register}
                      disabled
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}

              {procurement?.procurementDetails?.procurementDetailsDocuments
                ?.length ? (
                <div className="row">
                  <div className="col-md-12 col-lg-3">
                    <Text weight="semibold" size="sm" color="title">
                      {t(
                        "PROCUREMENT_CREATE.GENERAL_INFO.FORM.DETAILS_DOCUMENTS.LABEL",
                      )}
                    </Text>
                  </div>
                  <div className="col-md-12 col-lg-7">
                    <MultiFile
                      id="procurementDetailsDocuments"
                      control={control}
                      setValue={setValue}
                      register={register}
                      disabled
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}

              {procurement?.procurementDetails?.procurementWarrantyDocuments
                ?.length ? (
                <div className="row">
                  <div className="col-md-12 col-lg-3">
                    <Text weight="semibold" size="sm" color="title">
                      {t(
                        "PROCUREMENT_CREATE.GENERAL_INFO.FORM.WARRANTY_DOCUMENTS.LABEL",
                      )}
                    </Text>
                  </div>
                  <div className="col-md-12 col-lg-7">
                    <MultiFile
                      id="procurementWarrantyDocuments"
                      control={control}
                      setValue={setValue}
                      register={register}
                      disabled
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </>
          )}

          <InputTextRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.EXTRA_COST_INFO"
            register={{ ...register(PROCUREMENT_DETAILS.EXTRA_COST_INFO) }}
            error={errors as any}
          />

          <InputTextRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.EXTRA_COST_AMOUNT"
            register={{ ...register(PROCUREMENT_DETAILS.EXTRA_COST_AMOUNT) }}
            type="number"
            error={errors as any}
          />

          <MultiFileRow
            label="PROCUREMENT_CREATE.GENERAL_INFO.FORM.DOCUMENTS"
            name={PROCUREMENT_DETAILS.DOCUMENTS}
            control={control}
            setValue={setValue}
            register={register}
            fileDetails={["documentName", "date", "remarks", "memorialNo"]}
          />

          <ProcurementDemandList
            demands={procurement?.procurementDemands}
            selectedProducts={selectedProductsPerDemand}
            handleSelectedDemand={handleSelectedDemand}
            demandIds={demandIds}
          />

          <div className="py-10">
            <SelectedProductTable
              data={id ? procurement?.procurementItems : selectedProduct}
              disable={id ? true : false}
            />
          </div>

          {getAccess(roles, "PROCUREMENT_HR") && (
            <div className="text-end py-10">
              <Button
                fill="outline"
                type="light"
                size="sm"
                htmlType="submit"
                loading={isCreateLoading || isUpdateLoading}
              >
                {t("PROCUREMENT_CREATE.GENERAL_INFO.FORM.BUTTONS.SAVE")}
              </Button>
            </div>
          )}
        </form>
        <div className="text-end border-top py-10">
          {id && <NextButton action={() => changeActiveTab(1)} />}
        </div>
      </FormProvider>
    </div>
  );
};
